import type React from "react";
import { useEffect, useState } from "react";

import { Modal } from "../ui/modal";
import { Select } from "../ui/select";
import { Pager, useFitPages } from "../ui/paging";
import styles from "./operator-page.module.css";
import { clinicPayments, markPaid, removePayment } from "./operator-api";
import type { ClinicMonth, ClinicPaymentHistory } from "./operator-api";
import { priceCents, priceText } from "./price-format";
import { RequiredMark } from "../ui/required-mark";

/** `2026-10` → `October 2026`. */
export function monthName(month: string): string {
  return new Date(`${month}-01T00:00:00.000Z`).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** `2026-10-03` → `3 Oct 2026`. */
const dayName = (iso: string): string =>
  new Date(`${iso}T00:00:00.000Z`).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

const todayIso = (): string => new Date().toISOString().slice(0, 10);

/**
 * **A clinic's billing history, and marking a month paid** (BP-D1, BP-D2).
 *
 * Emile, 2026-10-17: *"I have to enter a clinic and set that they paid for this
 * month."* So each month is a row — its bill, and whether it was paid — and
 * *Mark as paid* records what arrived, defaulting to that month's bill. Records
 * only: nothing is charged or sent (BL-D1), and the subscription state is moved
 * on its own tab, by hand, as before (BL-D10). A month paid by mistake is put
 * back with *Remove*.
 *
 * Paged to the card, never a scroll.
 */
export function PaymentsPanel({ clinicId }: { clinicId: string }): React.JSX.Element {
  const [history, setHistory] = useState<ClinicPaymentHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [marking, setMarking] = useState<ClinicMonth | null>(null);
  const [removing, setRemoving] = useState<ClinicMonth | null>(null);
  /** The head's *Record a payment*: the dialog with every month to choose from. */
  const [recording, setRecording] = useState(false);

  useEffect(() => {
    void clinicPayments(clinicId)
      .then(setHistory)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not load the payments");
      });
  }, [clinicId]);

  const months = history?.months ?? [];
  const { areaRef, measure, page, setPage, pages, range } = useFitPages({
    keys: months.map((m) => m.month),
    estimate: () => 52,
    gap: 0,
    resetOn: clinicId,
  });

  if (history === null) {
    return error === null ? (
      <span className={styles.muted}>Loading…</span>
    ) : (
      <p className={styles.error} role="alert">
        {error}
      </p>
    );
  }

  const paidCount = months.filter((m) => m.payment !== null).length;
  const overdue = months.filter((m) => m.overdue);

  return (
    <section className={styles.paymentsPanel} data-testid="payments-panel">
      <div className={styles.panelHead}>
        <span className={styles.muted} data-testid="payments-summary">
          {paidCount} of {months.length} {months.length === 1 ? "month" : "months"} paid ·{" "}
          {priceText(history.paidCents)} received
          {overdue.length > 0 && (
            <>
              {" · "}
              <span className={styles.warnText}>
                {overdue.length} unpaid {overdue.length === 1 ? "month" : "months"}
                {history.owedCents > 0 && `, ${priceText(history.owedCents)}`}
              </span>
            </>
          )}
        </span>
        <button
          type="button"
          className={styles.primary}
          onClick={() => setRecording(true)}
          data-testid="record-payment"
        >
          Record a payment
        </button>
      </div>
      {error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      <div className={styles.ledgerHead} aria-hidden="true">
        <span>Month</span>
        <span>Bill</span>
        <span>Paid</span>
        <span />
      </div>
      <div className={styles.fitList} ref={areaRef}>
        <ul className={styles.rows} data-testid="payments-list">
          {months.slice(range[0], range[1]).map((m) => (
            <li
              key={m.month}
              ref={measure(m.month)}
              className={styles.ledgerRow}
              data-testid={`payment-${m.month}`}
            >
              <span className={styles.rowName}>{monthName(m.month)}</span>
              <span className={styles.figureCell}>
                {m.billCents === null ? (
                  <span className={styles.muted}>No price set</span>
                ) : (
                  priceText(m.billCents)
                )}
              </span>
              <span>
                {m.payment !== null ? (
                  <span className={styles.paidCell}>
                    <span className={styles.chipOk}>Paid {priceText(m.payment.amountCents)}</span>
                    <span className={styles.rowSub}>
                      {dayName(m.payment.paidOn)}
                      {m.payment.method === null ? "" : ` · ${m.payment.method}`}
                      {m.payment.recordedByName === null ? "" : ` · ${m.payment.recordedByName}`}
                    </span>
                  </span>
                ) : m.overdue ? (
                  <span className={styles.chipWarn}>Unpaid</span>
                ) : (
                  <span className={styles.muted}>This month</span>
                )}
              </span>
              <span className={styles.ledgerAction}>
                {m.payment === null ? (
                  <button
                    type="button"
                    className={m.overdue ? styles.primary : styles.ghost}
                    onClick={() => setMarking(m)}
                    data-testid={`mark-paid-${m.month}`}
                  >
                    Mark as paid
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className={styles.ghost}
                      onClick={() => setMarking(m)}
                      data-testid={`edit-payment-${m.month}`}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={styles.ghost}
                      onClick={() => setRemoving(m)}
                      data-testid={`remove-payment-${m.month}`}
                    >
                      Remove
                    </button>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      </div>
      <Pager
        page={page}
        count={pages.length}
        onPage={setPage}
        from={range[0] + 1}
        to={range[1]}
        total={months.length}
        noun="months"
        testId="payments-pager"
      />

      {(marking !== null || recording) && (
        <RecordPaymentDialog
          choices={(marking !== null ? [marking] : months).map((m) => ({
            key: m.month,
            label: monthName(m.month),
            billCents: m.billCents,
            payment: m.payment,
          }))}
          chooseLabel="For the month of"
          // The oldest unpaid month first: arrears are cleared in order.
          initialKey={
            marking?.month ??
            [...months].reverse().find((m) => m.payment === null)?.month ??
            months[0]?.month ??
            ""
          }
          onClose={() => {
            setMarking(null);
            setRecording(false);
          }}
          onSave={(month, body) =>
            markPaid(clinicId, month, body).then((next) => {
              setHistory(next);
              setMarking(null);
              setRecording(false);
            })
          }
        />
      )}

      {removing !== null && (
        <Modal
          label={`Remove the payment for ${monthName(removing.month)}`}
          onClose={() => setRemoving(null)}
        >
          <div className={styles.dialogBody}>
            <h2 className={styles.h2}>Remove the payment for {monthName(removing.month)}?</h2>
            <p className={styles.muted}>
              The month goes back to unpaid. Use this when a payment was recorded by mistake.
            </p>
            <div className={styles.dialogActions}>
              <button type="button" className={styles.ghost} onClick={() => setRemoving(null)}>
                Keep it
              </button>
              <button
                type="button"
                className={styles.danger}
                data-testid="remove-payment-confirm"
                onClick={() => {
                  void removePayment(clinicId, removing.month)
                    .then((next) => {
                      setHistory(next);
                      setRemoving(null);
                    })
                    .catch((cause: unknown) => {
                      setError(cause instanceof Error ? cause.message : "Could not remove it");
                      setRemoving(null);
                    });
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
}

/** Something a payment can be recorded against: a month, or a clinic's month. */
export interface PaymentChoice {
  key: string;
  label: string;
  billCents: number | null;
  payment: ClinicMonth["payment"];
}

/**
 * **Record a payment** (Emile, 2026-10-17): what arrived, when and how — the
 * amount the chosen month's bill unless changed. With more than one choice the
 * dialog first asks which month (the Payments tab) or which clinic (Accounting)
 * it is for; opened from a row, the row is the choice.
 */
export function RecordPaymentDialog({
  choices,
  chooseLabel,
  initialKey,
  onClose,
  onSave,
}: {
  choices: readonly PaymentChoice[];
  chooseLabel: string;
  initialKey: string;
  onClose: () => void;
  onSave: (
    key: string,
    body: { amountCents: number; paidOn: string; method: string; note: string },
  ) => Promise<void>;
}): React.JSX.Element {
  const [key, setKey] = useState(initialKey);
  const chosen = choices.find((c) => c.key === key) ?? choices[0];
  const startOf = (c: PaymentChoice | undefined): string => {
    const cents = c?.payment?.amountCents ?? c?.billCents ?? null;
    return cents === null ? "" : (cents / 100).toFixed(2);
  };
  const [amount, setAmount] = useState(startOf(chosen));
  const [touched, setTouched] = useState(false);
  const [paidOn, setPaidOn] = useState(chosen?.payment?.paidOn ?? todayIso());
  const [method, setMethod] = useState(chosen?.payment?.method ?? "");
  const [note, setNote] = useState(chosen?.payment?.note ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cents = priceCents(amount);
  const valid = chosen !== undefined && cents !== null && /^\d{4}-\d{2}-\d{2}$/.test(paidOn);
  const editing = chosen?.payment !== null && chosen?.payment !== undefined;

  return (
    <Modal label="Record a payment" onClose={onClose} testId="mark-paid-dialog">
      <div className={styles.dialogBody}>
        <h2 className={styles.h2}>
          {choices.length > 1
            ? "Record a payment"
            : editing
              ? `Edit ${chosen?.label ?? ""}'s payment`
              : `Mark ${chosen?.label ?? ""} as paid`}
        </h2>
        {choices.length > 1 && (
          <Select
            id="record-payment-for"
            label={chooseLabel}
            value={key}
            options={choices.map((c) => ({
              value: c.key,
              label: c.label,
              hint:
                c.payment !== null
                  ? "paid"
                  : c.billCents === null
                    ? "no price"
                    : priceText(c.billCents),
            }))}
            onChange={(next) => {
              setKey(next);
              const c = choices.find((x) => x.key === next);
              if (!touched) setAmount(startOf(c));
              if (c?.payment) {
                setPaidOn(c.payment.paidOn);
                setMethod(c.payment.method ?? "");
                setNote(c.payment.note ?? "");
              }
            }}
            testId="record-payment-for"
          />
        )}
        <p className={styles.muted}>
          {chosen === undefined || chosen.billCents === null
            ? "This bill has a line with no price, so type what arrived."
            : `The bill is ${priceText(chosen.billCents)}. Change the amount if a different sum arrived.`}
          {editing && " Already paid: saving replaces what was recorded."}
        </p>
        <div className={styles.pair}>
          <label className={styles.field}>
            <span>
              Amount received
              <RequiredMark />
            </span>
            <input
              value={amount}
              inputMode="decimal"
              onChange={(event) => {
                setAmount(event.target.value);
                setTouched(true);
              }}
              data-testid="mark-paid-amount"
            />
          </label>
          <label className={styles.field}>
            <span>
              Paid on
              <RequiredMark />
            </span>
            <input
              type="date"
              value={paidOn}
              onChange={(event) => setPaidOn(event.target.value)}
              data-testid="mark-paid-date"
            />
          </label>
        </div>
        <label className={styles.field}>
          <span>How</span>
          <input
            value={method}
            placeholder="e.g. bank transfer"
            onChange={(event) => setMethod(event.target.value)}
            data-testid="mark-paid-method"
          />
        </label>
        <label className={styles.field}>
          <span>Note</span>
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            data-testid="mark-paid-note"
          />
        </label>
        {error !== null && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
        <div className={styles.dialogActions}>
          <button type="button" className={styles.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.primary}
            disabled={!valid || busy}
            data-testid="mark-paid-save"
            onClick={() => {
              if (cents === null || chosen === undefined) return;
              setBusy(true);
              setError(null);
              void onSave(chosen.key, { amountCents: cents, paidOn, method, note })
                .catch((cause: unknown) => {
                  setError(cause instanceof Error ? cause.message : "Could not save");
                })
                .finally(() => setBusy(false));
            }}
          >
            {busy ? "Saving…" : editing ? "Save" : "Record payment"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
